import type { ConfiguratorInstance } from '../public-api';
import type { CharacteristicRef, ValueRef } from './instance';
import type { ValidationError } from '../validation/validate';

/**
 * Custom renderers: host code that draws a Characteristic itself.
 *
 * A Characteristic whose admin "CSS-Klasse" field contains a registered
 * renderer name gets an empty container instead of the built-in input. The
 * container's content belongs to the host — React renders it once and never
 * touches it again — while values still flow through the store, so rules,
 * prices, validation, wizard and payload keep working.
 *
 * Spec: v3/docs/v3-custom-renderer-spec.md.
 */
export interface CharacteristicRenderer {
    /** 'body' (default): Configento keeps title, info and errors. 'all': the renderer draws them too. */
    replace?: 'body' | 'all';
    /** Merge every visible Characteristic of the same Group carrying this class into one widget. */
    combine?: boolean;
    mount(el: HTMLElement, ctx: RendererContext): void;
    /** `el` is the container `mount` got — one renderer can serve several widgets on a page. */
    update?(ctx: RendererContext, el: HTMLElement): void;
    destroy?(el: HTMLElement): void;
}

export interface RendererValue {
    id: number;
    key: string;
    label: string;
    info: string | null;
    image: string | null;
    /** Surcharge in the shop's currency and tax basis, like the price display. */
    price: number;
    /** Formatted like the built-in views; '' when the Component shows no price for it. */
    priceLabel: string;
    selected: boolean;
}

export interface RendererCharacteristic {
    id: number;
    key: string;
    type: string;
    title: string;
    info: string | null;
    required: boolean;
    /** Allowed Values only — rule-blacklisted ones are left out. Empty for free-entry types. */
    values: RendererValue[];
    /** Same shape as `getValue()`. */
    selection: unknown;
    errors: ValidationError[];
    textConstraints?: { minLength?: number; maxLength?: number; validator?: string };
}

export interface RendererContext {
    characteristic: RendererCharacteristic;
    characteristics: RendererCharacteristic[];
    locale: string;
    instance: ConfiguratorInstance | null;
    selectValue(value: ValueRef | null, characteristic?: CharacteristicRef): void;
    toggleValue(value: ValueRef, characteristic?: CharacteristicRef): void;
    setText(text: string, characteristic?: CharacteristicRef): void;
    setQuantity(value: ValueRef, quantity: number, characteristic?: CharacteristicRef): void;
    reset(characteristic?: CharacteristicRef): void;
}

declare global {
    interface Window {
        /** Renderers a page registers before the bundle has loaded. */
        ConfigentoRenderers?: Record<string, CharacteristicRenderer | undefined>;
    }
}

const NAME = /^[a-z][a-z0-9-]*$/;
const registry = new Map<string, CharacteristicRenderer>();
const listeners = new Set<() => void>();
let version = 0;

function changed(): void {
    version++;
    for (const cb of listeners) cb();
}

export function registerRenderer(name: string, renderer: CharacteristicRenderer): () => void {
    if (!NAME.test(name)) {
        throw new Error(`Configento: renderer name "${name}" must be a CSS class name (a-z, 0-9, -).`);
    }
    if (!renderer || typeof renderer.mount !== 'function') {
        throw new Error(`Configento: renderer "${name}" needs a mount(el, ctx) function.`);
    }
    if (registry.has(name)) {
        console.warn(`[Configento] renderer "${name}" registered again — replacing the previous one.`);
    }
    registry.set(name, renderer);
    changed();
    return () => {
        if (registry.get(name) !== renderer) return;
        registry.delete(name);
        changed();
    };
}

/** Take over renderers stored in `window.ConfigentoRenderers` before the bundle ran. */
export function adoptPreloadedRenderers(): void {
    const preloaded = typeof window !== 'undefined' ? window.ConfigentoRenderers : undefined;
    if (!preloaded) return;
    for (const [name, renderer] of Object.entries(preloaded)) {
        if (!renderer) continue;
        try {
            registerRenderer(name, renderer);
        } catch (err) {
            console.error('[Configento]', err);
        }
    }
}

/** The renderer for a Characteristic's admin CSS classes, first registered match wins. */
export function rendererFor(cssClass: string | undefined): { name: string; renderer: CharacteristicRenderer } | null {
    if (!cssClass || registry.size === 0) return null;
    for (const name of cssClass.split(/\s+/)) {
        const renderer = registry.get(name);
        if (renderer) return { name, renderer };
    }
    return null;
}

export function subscribeRenderers(cb: () => void): () => void {
    listeners.add(cb);
    return () => listeners.delete(cb);
}

export function getRenderersVersion(): number {
    return version;
}

/** Test hook: forget every renderer. */
export function resetRenderers(): void {
    registry.clear();
    changed();
}
